// Connect a WhatsApp Business number — two paths:
//   1. "Continue with Facebook" → Meta Embedded Signup via the Facebook JS SDK
//      (FB.login with config_id). This is the ONLY launch method Meta runs the
//      WABA-creation wizard for. The wizard returns waba_id + phone_number_id +
//      an exchangeable code via the WA_EMBEDDED_SIGNUP window message; we send
//      all three to the backend, which exchanges the code (no redirect_uri) and
//      onboards that exact WABA.
//   2. "Add existing account details" → token/ids → backend.
// The backend exchanges/validates, subscribes webhooks, registers the number
// and saves the connection; this component just drives the UI and reports steps.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, AlertTriangle, XCircle, Loader2, KeyRound, Copy, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFacebookSdk } from "@/hooks/useFacebookSdk";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function api(path, options = {}) {
  const res = await fetch(`${BACKEND_URL}/api${path}`, { credentials: "include", headers: { "Content-Type": "application/json" }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.detail || `Request failed (${res.status})`); e.data = data; throw e; }
  return data;
}

function FacebookGlyph({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
    </svg>
  );
}

function StepList({ steps }) {
  if (!steps?.length) return null;
  const Icon = { ok: CheckCircle, warn: AlertTriangle, error: XCircle };
  const cls = { ok: "text-emerald-600", warn: "text-amber-600", error: "text-rose-600" };
  return (
    <ul className="space-y-2">
      {steps.map((s) => { const I = Icon[s.status] || AlertTriangle; return (
        <li key={s.key} className="flex items-start gap-2 text-sm">
          <I className={`mt-0.5 h-4 w-4 shrink-0 ${cls[s.status] || "text-slate-400"}`} />
          <div><div className="font-medium text-slate-800">{s.label}</div>{s.detail && <div className="text-xs text-slate-500">{s.detail}</div>}</div>
        </li>
      ); })}
    </ul>
  );
}

function CopyField({ label, value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
        <div className="truncate font-mono text-xs text-slate-700">{value || "—"}</div>
      </div>
      {value && (
        <button type="button" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Copy">
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function WhatsAppConnect({ currentUser, businessId, onConnected, initialManualOpen = false }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { steps, integration, needs_pin, pin_set }
  const [pin, setPin] = useState("");
  const [showManual, setShowManual] = useState(!!initialManualOpen);
  const [showAdmin, setShowAdmin] = useState(false);
  const [manual, setManual] = useState({ phone_number_id: "", waba_id: "", access_token: "", pin: "" });
  const esData = useRef({ waba_id: "", phone_number_id: "" });
  const [popupOpen, setPopupOpen] = useState(false);
  const isSuperAdmin = currentUser?.role === "super_admin";
  const { ready: fbReady, error: fbError } = useFacebookSdk(cfg?.configured ? cfg.app_id : null, cfg?.graph_version);

  useEffect(() => {
    const q = businessId ? `?business_id=${encodeURIComponent(businessId)}` : "";
    api(`/whatsapp/embedded-signup/config${q}`).then(setCfg).catch((e) => { setCfg({ configured: false, error: e.message }); });
  }, [businessId]);

  const complete = useCallback(async (payload) => {
    setBusy(true); setResult(null);
    try {
      const d = await api("/whatsapp/embedded-signup/complete", { method: "POST", body: JSON.stringify({ ...payload, business_id: businessId || undefined }) });
      setResult(d);
      toast.success(`Connected ${d.integration?.display_name || d.integration?.phone_number || "WhatsApp number"}`);
      onConnected?.(d.integration);
    } catch (e) {
      setResult({ error: e.message, steps: e.data?.steps || [], tried: e.data?.tried || [] });
      toast.error(e.message);
    } finally { setBusy(false); }
  }, [onConnected, businessId]);

  // Embedded Signup (the only launch method Meta runs the WABA-creation wizard
  // for) delivers waba_id + phone_number_id + the exchangeable code via the
  // WA_EMBEDDED_SIGNUP window message. Capture the ids here; the code arrives in
  // the FB.login callback below. Requires our domain in the app's Allowed
  // Domains (JS SDK) and Valid OAuth Redirect URIs.
  useEffect(() => {
    const onMsg = (event) => {
      let host = "";
      try { host = new URL(event.origin).hostname; } catch { return; }
      if (!/(^|\.)facebook\.com$/.test(host)) return;
      let d;
      try { d = typeof event.data === "string" ? JSON.parse(event.data) : event.data; } catch { return; }
      if (!d || d.type !== "WA_EMBEDDED_SIGNUP") return;
      if (String(d.event || "").startsWith("FINISH")) {
        esData.current = { waba_id: d.data?.waba_id || "", phone_number_id: d.data?.phone_number_id || "" };
      } else if (d.event === "CANCEL") {
        setBusy(false); setPopupOpen(false);
        toast.message(`Setup was cancelled${d.data?.current_step ? ` at "${d.data.current_step}"` : ""}. Nothing was connected.`);
      } else if (d.event === "ERROR") {
        setBusy(false); setPopupOpen(false);
        toast.error(d.data?.error_message || "Facebook reported an error during setup");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const startEmbeddedSignup = () => {
    if (!cfg?.configured) return;
    if (!window.FB) return toast.error("Facebook is still loading — try again in a moment");
    esData.current = { waba_id: "", phone_number_id: "" };
    setResult(null);
    setPopupOpen(true);
    window.FB.login((response) => {
      const code = response?.authResponse?.code;
      if (!code) { setPopupOpen(false); toast.message("Facebook sign-in was not completed. Nothing was connected."); return; }
      // The FINISH message with waba_id/phone_number_id normally lands just
      // before this callback; give it a brief moment (the code is valid ~30s).
      setPopupOpen(false);
      setTimeout(() => complete({ code, waba_id: esData.current.waba_id, phone_number_id: esData.current.phone_number_id }), 250);
    }, {
      config_id: cfg.config_id,
      response_type: "code",
      override_default_response_type: true,
      extras: { setup: {}, featureType: "", sessionInfoVersion: "3" },
    });
  };

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.access_token.trim() || (!manual.phone_number_id.trim() && !manual.waba_id.trim())) return toast.error("Access token plus a Phone Number ID or WABA ID are required");
    complete({ access_token: manual.access_token.trim(), phone_number_id: manual.phone_number_id.trim(), waba_id: manual.waba_id.trim(), pin: manual.pin.trim() || undefined });
  };

  const submitPin = async () => {
    if (!/^\d{6}$/.test(pin)) return toast.error("Enter the 6-digit PIN");
    setBusy(true);
    try {
      const d = await api(`/whatsapp/integrations/${result.integration.integration_id}/register`, { method: "POST", body: JSON.stringify({ pin }) });
      setResult((r) => ({ ...r, needs_pin: false, integration: d.integration, steps: (r.steps || []).map((s) => (s.key === "register" ? { ...s, status: "ok", detail: "Registered with your PIN" } : s)) }));
      toast.success("Number registered");
      onConnected?.(d.integration);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const configured = !!cfg?.configured;

  return (
    <div className="space-y-4" data-testid="wa-connect">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Connect a WhatsApp Business number</h2>
        <p className="text-sm text-slate-500">Directly on Meta's WhatsApp Cloud API. No third-party gateway. Free to apply.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Path 1: Embedded Signup */}
        <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Set up with Meta</div>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                Sign in with the Facebook account that manages your business. Meta walks you through creating or selecting your WhatsApp Business Account and phone number — we take it from there.
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200">Recommended</span>
          </div>
          <ul className="mt-3 space-y-1 text-xs text-slate-600">
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-3.5 w-3.5 text-emerald-500" /> New or existing WhatsApp Business Account</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-3.5 w-3.5 text-emerald-500" /> Webhooks and number registration done automatically</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-3.5 w-3.5 text-emerald-500" /> Access token stays on the server, never in the browser</li>
          </ul>
          <div className="mt-auto pt-4">
            <button
              type="button"
              onClick={startEmbeddedSignup}
              disabled={!configured || !fbReady || busy || popupOpen}
              data-testid="wa-continue-with-facebook"
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#1877F2] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#166FE5] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy || popupOpen ? <Loader2 className="h-4 w-4 animate-spin" /> : <FacebookGlyph />}
              {busy ? "Finishing…" : popupOpen ? "Complete the steps in the Facebook window…" : "Continue with Facebook"}
            </button>
            {!cfg && <p className="mt-2 text-[11px] text-slate-400">Checking configuration…</p>}
            {cfg && !configured && (
              <p className="mt-2 text-[11px] text-amber-700">
                Not available yet — the platform's Meta app isn't configured on this server.{isSuperAdmin ? " See the setup checklist below." : " Ask your Dnexus administrator, or add your account details on the right."}
              </p>
            )}
            {configured && fbError && <p className="mt-2 text-[11px] text-rose-600">{fbError.message}</p>}
            {configured && !fbReady && !fbError && <p className="mt-2 text-[11px] text-slate-400">Loading Facebook…</p>}
            {configured && fbReady && (
              <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                Facebook opens a secure pop-up. Step through it to create or select your WhatsApp Business Account and add &amp; verify a phone number, then it closes on its own. <b>Best done on a computer</b> — some mobile browsers block the pop-up.
              </p>
            )}
          </div>
        </div>

        {/* Path 2: existing account */}
        <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-semibold text-slate-900">Add existing WhatsApp account details</div>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Already have a WhatsApp Business Account with a Cloud API number? Paste its details from Meta Business Manager. We'll verify, subscribe webhooks and register it the same way.
          </p>
          {!showManual ? (
            <div className="mt-auto pt-4">
              <Button variant="outline" onClick={() => setShowManual(true)} data-testid="wa-manual-toggle" className="h-11 w-full rounded-xl border-slate-200"><KeyRound className="mr-2 h-4 w-4" /> Enter account details</Button>
            </div>
          ) : (
            <form onSubmit={submitManual} className="mt-3 space-y-2.5">
              <input value={manual.phone_number_id} onChange={(e) => setManual((m) => ({ ...m, phone_number_id: e.target.value }))} placeholder="Phone Number ID" data-testid="wa-manual-phone-id" className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" />
              <input value={manual.waba_id} onChange={(e) => setManual((m) => ({ ...m, waba_id: e.target.value }))} placeholder="WhatsApp Business Account ID (WABA)" data-testid="wa-manual-waba" className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" />
              <input value={manual.access_token} onChange={(e) => setManual((m) => ({ ...m, access_token: e.target.value }))} placeholder="Permanent access token (System User)" type="password" data-testid="wa-manual-token" className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" />
              <input value={manual.pin} onChange={(e) => setManual((m) => ({ ...m, pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="Two-step PIN (6 digits, if already set)" inputMode="numeric" className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" />
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setShowManual(false)} className="rounded-lg">Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="wa-manual-submit" className="flex-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & connect"}</Button>
              </div>
              {cfg?.webhook_url && (
                <p className="text-[11px] text-slate-400">If this token belongs to your own Meta app, point that app's webhook to <span className="font-mono">{cfg.webhook_url}</span>.</p>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={`rounded-2xl border p-5 ${result.error ? "border-rose-200 bg-rose-50/40" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="wa-connect-result">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            {result.error ? <XCircle className="h-4 w-4 text-rose-600" /> : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
            {result.error ? "Connection failed" : `Connected: ${result.integration?.display_name || result.integration?.phone_number || "WhatsApp number"}`}
          </div>
          {result.error && <p className="mt-1 text-sm text-rose-700">{result.error}</p>}
          {result.tried?.length > 0 && (
            <ul className="mt-2 space-y-0.5 font-mono text-[11px] text-slate-500">
              {result.tried.map((t) => <li key={t}>{t}</li>)}
            </ul>
          )}
          <div className="mt-3"><StepList steps={result.steps} /></div>
          {result.pin_set && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              A new two-step verification PIN was set for this number: <span className="font-mono text-sm font-bold">{result.pin_set}</span>. Save it — Meta asks for it if the number is ever re-registered.
            </div>
          )}
          {result.needs_pin && result.integration && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
              <span className="text-xs text-amber-800">Enter this number's existing two-step verification PIN to finish:</span>
              <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit PIN" className="h-9 w-32 rounded-lg border border-amber-300 px-2 text-sm" data-testid="wa-pin" />
              <Button onClick={submitPin} disabled={busy} size="sm" className="rounded-lg bg-amber-600 text-white hover:bg-amber-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Register"}</Button>
            </div>
          )}
        </div>
      )}

      {/* Platform setup checklist (super admin) */}
      {isSuperAdmin && cfg && (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <button type="button" onClick={() => setShowAdmin((v) => !v)} className="flex w-full items-center justify-between px-5 py-3 text-left">
            <span className="text-sm font-semibold text-slate-800">Platform setup (Dnexus Meta app) {configured ? <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">configured</span> : <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">missing: {(cfg.missing || []).join(", ") || "—"}</span>}</span>
            {showAdmin ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </button>
          {showAdmin && (
            <div className="space-y-4 border-t border-slate-100 px-5 py-4 text-sm text-slate-600">
              <ol className="list-decimal space-y-1.5 pl-5 text-xs leading-relaxed">
                <li>In <b>developers.facebook.com</b> create (or reuse) a Business-type app owned by Dnexus's Business Manager. Add the <b>WhatsApp</b> product and <b>Facebook Login for Business</b>.</li>
                <li>Facebook Login for Business → <b>Configurations</b> → create one of type <b>WhatsApp Embedded Signup</b> (permissions <code>whatsapp_business_management</code>, <code>whatsapp_business_messaging</code>, <code>business_management</code>). Copy its <b>Configuration ID</b>.</li>
                <li>Facebook Login for Business → Settings → <b>Valid OAuth Redirect URIs</b>: add the <b>login callback URL</b> below (required — Strict Mode rejects any redirect that isn't listed here; App Domains alone is not enough).</li>
                <li>WhatsApp → Configuration → Webhook: webhook URL below, verify token of your choice, subscribe to <b>messages</b>. Copy App ID and App Secret from Settings → Basic.</li>
                <li>Set on the server (<code>/var/www/crm-backend/.env</code>) and restart: <code>META_APP_ID</code>, <code>META_APP_SECRET</code>, <code>META_ES_CONFIG_ID</code>, <code>META_WEBHOOK_VERIFY_TOKEN</code>.</li>
                <li>Until the app has <b>Advanced Access</b> for the two WhatsApp permissions (App Review / Tech Provider verification), only people with a role on the app can complete "Continue with Facebook". Other businesses can still use "Add existing account details".</li>
              </ol>
              <div className="grid gap-3 sm:grid-cols-2">
                <CopyField label="Login callback URL (Valid OAuth Redirect URIs)" value={cfg.callback_url} />
                <CopyField label="Webhook URL" value={cfg.webhook_url} />
                <CopyField label="Verify token" value={cfg.verify_token || (cfg.verify_token_set ? "(set)" : "")} />
                <CopyField label="App ID" value={cfg.app_id} />
                <CopyField label="Embedded Signup Configuration ID" value={cfg.config_id} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
